import { PlusCircleIcon } from '@heroicons/react/24/solid';
import React, { useState } from 'react';

import { InviteUser } from '@/components/legacy-ui/InviteUser';
import { Button } from '@/components/ui/button';
import { CustomerData } from '@/sections/customerManagement/types';
import { Users } from '@/sections/Users';
import { useAuth } from '@/state/auth';

interface CustomerUsersTabProps {
  customer: CustomerData;
}

/**
 * CustomerUsersTab - Displays and manages authorized users for a customer account
 *
 * Allows viewing authorized users and adding new users via impersonation.
 * The Add User button triggers the InviteUser modal with customer context.
 */
export const CustomerUsersTab: React.FC<CustomerUsersTabProps> = ({
  customer,
}) => {
  const { userType } = useAuth();
  const [showInviteModal, setShowInviteModal] = useState(false);

  // Defense in depth - customer drawer should only be accessible to Praetorian users
  if (userType !== 'praetorian') {
    return null;
  }

  return (
    <div>
      <div className="mb-4 flex flex-row items-start justify-between gap-4">
        <div className="flex-1">
          <h2 className="text-lg font-bold">Authorized Users</h2>
          <p className="text-chariot-text-secondary text-sm">
            These tenants, including their SSO users, have access to your
            account.
          </p>
        </div>
        <Button
          onClick={() => setShowInviteModal(true)}
          size="sm"
          aria-label="Add authorized user to customer account"
          data-testid="customer-add-user-button"
        >
          <PlusCircleIcon className="size-4" />
          Add User
        </Button>
      </div>

      <Users customerEmail={customer.email} variant="compact" />

      <InviteUser
        open={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        customerEmail={customer.email}
      />
    </div>
  );
};
